import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useActiveStudent } from '../hooks/useActiveStudent';
import { useAppDispatch } from '../hooks/redux';
import { switchMembership } from '../store/slices/authSlice';
import { useSwitchContextMutation } from '../store/api/localApi';
import { useSyncParentContext } from '../hooks/useSyncParentContext';

/**
 * Guard for /parent/* routes.
 *
 * Four jobs:
 *
 *   1. Non-parent active role → kick to /dashboard. Staff/admin who switch
 *      roles in the role-switcher will already have re-routed; this is the
 *      defence in depth for direct-URL / shared-link cases.
 *
 *   2. Parent landed on a /parent route with an unknown studentId →
 *      redirect to the parent's home (parentHomePath). The "unknown" case
 *      covers a few real situations:
 *        - kid was deleted/transferred while the URL was bookmarked
 *        - parent shared a link with a co-parent who doesn't share that
 *          kid (rare but possible in blended families)
 *        - URL typo
 *
 *   3. Loading: render a thin loading state. /me/children resolves quickly
 *      and avoids a flash of wrong-content while the picker query is in
 *      flight.
 *
 *   4. Keep the active PARENT membership (and so X-Active-Context) at the
 *      URL child's location. Every way onto a child page — the remembered
 *      child at launch, a deep link, a pick from a family page — lands here,
 *      so this is the one place that guarantees location-scoped PARENT
 *      abilities (closures, form fill, policy confirmation) see the right
 *      location.
 */
const ParentRoute: React.FC = () => {
  const { isParent, user, activeLocationId } = useAuth();
  const { activeStudent, children, isLoading, parentHomePath } = useActiveStudent();
  const params = useParams<{ studentId?: string }>();
  const dispatch = useAppDispatch();
  const [switchContext] = useSwitchContextMutation();
  // Snapshot whether the user was already a parent at mount time. The
  // auto-switch only runs when the answer is "no": a hybrid user landed on
  // /parent/* with a non-PARENT active role. Users who were parents at mount
  // and *later* switch away (via the role picker) won't trigger the auto-
  // switch, so they actually leave the parent subtree.
  const wasParentAtMountRef = useRef(isParent);
  // Track whether the one-time auto-switch effect has run. Used to
  // distinguish "still loading the auto-switch" from "user explicitly
  // chose another role after the auto-switch."
  const autoSwitchAttemptedRef = useRef(false);

  // Hybrid users (e.g. LOCATION_ADMIN who is also a parent) may land on a
  // /parent/* URL while their active membership is still on the staff/admin
  // role. Auto-switch into a PARENT membership so the kid-as-frame nav and
  // backend permissions line up.
  const parentMembershipIndex = (user?.memberships ?? []).findIndex((m) => m.role === 'PARENT');

  useEffect(() => {
    if (
      isParent ||
      wasParentAtMountRef.current ||
      autoSwitchAttemptedRef.current ||
      parentMembershipIndex < 0 ||
      !user
    ) {
      return;
    }
    autoSwitchAttemptedRef.current = true;
    const target = user.memberships[parentMembershipIndex];
    dispatch(switchMembership(parentMembershipIndex));
    switchContext({
      role: 'PARENT',
      organizationId: target.organizationId || undefined,
      locationId: target.locationId || undefined,
    }).catch(() => {
      // Backend rejected — not much to do; the redux switch still gives the
      // parent UI locally, and the next API call will surface the auth error.
    });
  }, [isParent, parentMembershipIndex, user, dispatch, switchContext]);

  // Job 4. Runs when the URL child (or its location) changes, not when the
  // active membership does: the membership picker switches first and navigates
  // after, and re-syncing to the old child in between would undo its switch.
  // Keying on the child also means a rejected switch (which reverts the
  // membership) is not retried in a loop. The key is forgotten on a page with
  // no child (/parent/choose, /parent/family/*): this route stays mounted
  // there, and a membership switch made meanwhile must not hide the return.
  const syncParentContext = useSyncParentContext();
  const childLocationId = activeStudent?.locationId;
  const childOrganizationId = activeStudent?.organizationId;
  const lastSyncedChildRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeStudent) {
      lastSyncedChildRef.current = null;
      return;
    }
    if (!isParent || !childLocationId) return;
    const childKey = `${activeStudent.id}:${childLocationId}`;
    if (lastSyncedChildRef.current === childKey) return;
    lastSyncedChildRef.current = childKey;
    if (childLocationId !== activeLocationId) {
      void syncParentContext(childLocationId, childOrganizationId);
    }
  }, [isParent, activeStudent, childLocationId, childOrganizationId, activeLocationId, syncParentContext]);

  // No parent membership at all → genuinely not authorized for /parent/*.
  if (!isParent && parentMembershipIndex < 0) {
    return <Navigate to="/dashboard" replace />;
  }

  // The user has a parent membership but it isn't active. Three cases:
  //   (a) They were a parent at mount and have since switched away (e.g.
  //       picked Location Admin in the role dropdown). Honour that and
  //       redirect them out — we never yank them back.
  //   (b) They weren't a parent at mount and the auto-switch hasn't run
  //       yet. Show a thin placeholder for the tick before the effect
  //       dispatches the switch.
  //   (c) They weren't a parent at mount, the auto-switch fired but the
  //       Redux state is still flipping. Same placeholder — next render
  //       will have isParent === true.
  if (!isParent) {
    if (wasParentAtMountRef.current) {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Switching to parent view…
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  // 0 children: route to onboarding empty-state (this should be rare —
  // a parent without any linked children probably hasn't accepted their
  // invite yet, in which case they wouldn't be authenticated at all).
  if (children.length === 0) {
    if (params.studentId !== 'onboarding') {
      return <Navigate to="/parent/onboarding" replace />;
    }
    return <Outlet />;
  }

  // URL has a studentId that doesn't match any of our kids. Redirect to the
  // right home (the remembered child, or the chooser) rather than 404.
  if (params.studentId && !activeStudent && params.studentId !== 'onboarding') {
    return <Navigate to={parentHomePath ?? '/parent/choose'} replace />;
  }

  return <Outlet />;
};

/**
 * Lands the parent on the right home view: their only child, the remembered
 * child, the chooser, or onboarding. Used as the index element for /parent so
 * a bare "/parent" URL ends up somewhere meaningful instead of 404.
 */
export const ParentHomeRedirect: React.FC = () => {
  const { parentHomePath, isLoading } = useActiveStudent();
  if (isLoading || !parentHomePath) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  return <Navigate to={parentHomePath} replace />;
};

export default ParentRoute;
